import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { AttendanceEventSourceBadge } from './AttendanceEventSourceBadge';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import type { AttendancePunchSource } from '../types/attendance';

type Props = {
  label: string;
  value: string;
  palette: ColorPalette;
  highlighted?: boolean;
  expandable?: boolean;
  link?: boolean;
  source?: AttendancePunchSource | null;
  treatAsManualEdit?: boolean;
  editedTime?: string | null;
};

const COLLAPSED_LINES = 2;
const EXPAND_AT_LENGTH = 72;

export function ApprovalDetailField({
  label,
  value,
  palette,
  highlighted = false,
  expandable = false,
  link = false,
  source = null,
  treatAsManualEdit = false,
  editedTime,
}: Props) {
  const { isCompact } = useResponsiveLayout();
  const [expanded, setExpanded] = useState(false);
  const canExpand = expandable && value.trim().length > EXPAND_AT_LENGTH;
  const collapsed = canExpand && !expanded;

  const content = (
    <>
      <View style={[styles.labelBlock, isCompact ? styles.labelBlockStacked : null]}>
        <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
        {highlighted ? (
          <Text style={[styles.submittedTag, { color: palette.info }]}>
            Submitted
          </Text>
        ) : null}
      </View>
      <View style={[styles.valueBlock, isCompact ? styles.valueBlockStacked : null]}>
        <Text
          style={[
            styles.value,
            isCompact ? styles.valueStacked : null,
            {
              color: link ? palette.info : palette.textPrimary,
              fontWeight: highlighted ? '700' : '500',
            },
          ]}
          numberOfLines={collapsed ? COLLAPSED_LINES : undefined}
          ellipsizeMode="tail"
          {...(Platform.OS === 'web' ? { title: value } : null)}
        >
          {value}
        </Text>
        {source ? (
          <AttendanceEventSourceBadge
            punch={source}
            treatAsManualEdit={treatAsManualEdit}
            editedTime={editedTime ?? value}
            align={isCompact ? 'left' : 'right'}
          />
        ) : null}
      </View>
    </>
  );

  const containerStyle = [
    styles.field,
    isCompact ? styles.fieldStacked : null,
    { borderBottomColor: palette.border },
    highlighted
      ? {
          backgroundColor: `${palette.info}14`,
          borderLeftColor: palette.info,
        }
      : null,
    highlighted ? styles.highlightedField : null,
  ];

  if (canExpand) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${label}: ${value}`}
        accessibilityHint={expanded ? 'Collapse text' : 'Expand full text'}
        onPress={() => setExpanded(current => !current)}
        style={containerStyle}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      accessibilityLabel={
        highlighted ? `${label}, submitted for correction` : label
      }
      style={containerStyle}
    >
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  field: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    minWidth: 0,
    maxWidth: '100%',
  },
  fieldStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  highlightedField: {
    marginHorizontal: -spacing.lg,
    paddingHorizontal: spacing.lg,
    borderLeftWidth: 3,
  },
  labelBlock: { flexShrink: 0, gap: 2, maxWidth: '42%', minWidth: 0 },
  labelBlockStacked: { maxWidth: '100%' },
  label: { ...typography.caption, flexShrink: 0 },
  submittedTag: { ...typography.caption, fontWeight: '700' },
  valueBlock: { flex: 1, alignItems: 'flex-end', gap: 4, minWidth: 0 },
  valueBlockStacked: { alignItems: 'flex-start', width: '100%' },
  value: { ...typography.body, textAlign: 'right', fontWeight: '500' },
  valueStacked: { textAlign: 'left' },
});
